import { useState, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Save } from 'lucide-react';
import { motion } from 'framer-motion';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import app from '../../firebase';
import { getMe, updateMe } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { Avatar } from '../Avatar';
import { useUserStore } from '../../store/userStore';
import type { FunctionalRole } from '../../types';
import { track } from '../../lib/telemetry';
import { isPersonaGated } from '../../lib/personas';

const storage = getStorage(app);
const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

const ROLE_LABELS: Record<FunctionalRole, string> = {
  executive: 'Executive',
  product_manager: 'Product Manager',
  team_lead: 'Team Lead',
  engineer: 'Engineer',
  designer: 'Designer',
  sales: 'Sales',
  marketing: 'Marketing',
};

export function MySettings() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const { activeRole, setActiveRole, setUser, sessionConfig } = useUserStore();
  const { data: user, isLoading } = useQuery({
    queryKey: queryKeys.me,
    queryFn: getMe,
  });
  // Merge JWT claims (sessionConfig) with Firestore profile (user) so that roles
  // assigned by an admin appear immediately without requiring a token refresh.
  const jwtRoles: FunctionalRole[] = sessionConfig?.functionalRoles ?? [];
  const firestoreRoles: FunctionalRole[] = user?.functionalRoles ?? [];
  const functionalRoles: FunctionalRole[] = [
    ...new Set([...jwtRoles, ...firestoreRoles]),
  ] as FunctionalRole[];

  // Persona consolidation (2026-08): only offer the three personas that
  // actually have dashboards (frontend/src/lib/personas.ts is the single
  // source of truth, shared with the nav and dashboard dispatch). A role a
  // user already holds but that's now gated (e.g. `engineer`) simply isn't
  // offered as a Primary Role option here — it stays in `functionalRoles`
  // untouched, so it isn't lost, and the backend coerces a gated
  // `primaryRole` to Product Manager on write (see users.py update_me), so
  // no already-assigned user can be left pointing at an option that no
  // longer renders.
  const legacyPersonasEnabled = sessionConfig?.legacyPersonasEnabled ?? false;
  const selectableRoles = functionalRoles.filter(
    (role) => !isPersonaGated(role, legacyPersonasEnabled),
  );
  // A persisted `activeRole` (view-mode choice, from before this rollout)
  // could itself be a now-gated role. Falling back through it unfiltered
  // would compare against options that no longer render, leaving no button
  // highlighted at all — treat it the same as unset instead.
  const effectiveActiveRole =
    activeRole && !isPersonaGated(activeRole, legacyPersonasEnabled) ? activeRole : null;

  const [form, setForm] = useState({
    fullName: '',
    department: '',
    alertEmail: true,
    alertInApp: true,
    alertSlack: false,
  });
  const [initialized, setInitialized] = useState(false);
  const savedFormRef = useRef({ fullName: '', department: '', alertEmail: true, alertInApp: true, alertSlack: false });

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);

  const handleAvatarChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setAvatarError(null);
    if (!file.type.startsWith('image/')) {
      setAvatarError('Please choose an image file.');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError('Image must be smaller than 5MB.');
      return;
    }
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  }, []);

  if (user && !initialized) {
    const initial = {
      fullName: user.fullName,
      department: user.department ?? '',
      alertEmail: user.alertPreferences?.email ?? true,
      alertInApp: user.alertPreferences?.inApp ?? true,
      alertSlack: user.alertPreferences?.slack ?? false,
    };
    setForm(initial);
    savedFormRef.current = initial;
    setInitialized(true);
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const changedFields: string[] = [];
      if (form.fullName !== savedFormRef.current.fullName) changedFields.push('fullName');
      if (form.department !== savedFormRef.current.department) changedFields.push('department');
      if (form.alertEmail !== savedFormRef.current.alertEmail) changedFields.push('alertEmail');
      if (form.alertInApp !== savedFormRef.current.alertInApp) changedFields.push('alertInApp');
      if (form.alertSlack !== savedFormRef.current.alertSlack) changedFields.push('alertSlack');

      // A manually-uploaded avatar always takes precedence over provider-derived
      // images (Google, ...) — see useAuth.ts for the sync-side of this rule.
      let avatarUpdate: { profilePictureUrl: string; avatarSource: 'manual' } | undefined;
      if (avatarFile && user?.userId) {
        try {
          const path = `avatars/${user.userId}/avatar`;
          const fileRef2 = storageRef(storage, path);
          await uploadBytes(fileRef2, avatarFile, { contentType: avatarFile.type });
          const url = await getDownloadURL(fileRef2);
          avatarUpdate = { profilePictureUrl: url, avatarSource: 'manual' };
          changedFields.push('avatar');
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          setAvatarError('Avatar upload failed: ' + msg);
          throw err;
        }
      }

      track('settings.profile_saved', 'action', { changedFields: changedFields.join(',') });
      return updateMe({
        fullName: form.fullName,
        department: form.department || null,
        alertPreferences: {
          email: form.alertEmail,
          inApp: form.alertInApp,
          slack: form.alertSlack,
        },
        ...avatarUpdate,
      });
    },
    onSuccess: (updatedUser) => {
      savedFormRef.current = { ...form };
      setAvatarFile(null);
      setAvatarPreview(null);
      setUser(updatedUser);
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });

  const primaryRoleMutation = useMutation({
    mutationFn: (role: FunctionalRole) => updateMe({ primaryRole: role }),
    onSuccess: (updatedUser) => {
      setUser(updatedUser);
      void qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });

  const handleRoleSelect = useCallback((role: FunctionalRole) => {
    setActiveRole(role);
    primaryRoleMutation.mutate(role);
  }, [setActiveRole, primaryRoleMutation]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="skeleton h-10 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">
      {/* Profile on the left, preferences on the right at xl — the single
          narrow column wasted two thirds of a wide viewport. Save + status
          stay full-width below since they submit the whole form. */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-x-12 gap-y-6 items-start">
      <div className="space-y-6 min-w-0">
      <div className="flex items-center gap-4">
        <div className="relative">
          <Avatar user={avatarPreview && user ? { ...user, profilePictureUrl: avatarPreview } : user} size="xl" />
          <button
            onClick={() => fileRef.current?.click()}
            className="absolute bottom-0 right-0 h-6 w-6 rounded-full bg-primary flex items-center justify-center border-2 border-bg-surface hover:bg-primary/80 transition-colors"
          >
            <Camera className="h-4 w-4 text-white" />
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleAvatarChange}
          />
        </div>
        <div>
          <p className="text-body font-medium text-gray-900 dark:text-white">{user?.fullName}</p>
          <p className="text-meta text-gray-500 dark:text-gray-400">{user?.email}</p>
          <p className="text-meta text-gray-500 capitalize mt-0.5">
            {user?.functionalRoles?.length
              ? user.functionalRoles.join(', ').replace(/_/g, ' ')
              : user?.role?.replace(/_/g, ' ') ?? ''}
          </p>
          {avatarFile && (
            <p className="text-meta text-primary mt-0.5">New avatar ready to save</p>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">
            Full Name
          </label>
          <input
            type="text"
            value={form.fullName}
            onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
          />
        </div>

        <div>
          <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">Email</label>
          <input
            type="email"
            value={user?.email ?? ''}
            disabled
            className="w-full px-3 py-2 rounded-lg bg-gray-100 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-400 dark:text-gray-500 text-meta cursor-not-allowed"
          />
        </div>

        <div>
          <label className="block text-meta text-gray-500 dark:text-gray-400 mb-1.5">
            Department
          </label>
          <input
            type="text"
            value={form.department}
            onChange={(e) =>
              setForm((p) => ({ ...p, department: e.target.value }))
            }
            placeholder="e.g. Engineering, Product"
            className="w-full px-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
          />
        </div>
      </div>
      </div>

      <div className="space-y-6 min-w-0">
      <div>
        <h4 className="text-title text-gray-900 dark:text-white mb-6">
          Alert Preferences
        </h4>
        <div className="space-y-4">
          {(
            [
              { key: 'alertEmail', label: 'Email notifications', channel: 'email' },
              { key: 'alertInApp', label: 'In-app notifications', channel: 'inApp' },
              { key: 'alertSlack', label: 'Slack notifications', channel: 'slack' },
            ] as const
          ).map(({ key, label, channel }) => (
            <label
              key={key}
              className="flex items-center justify-between cursor-pointer"
            >
              <span className="text-meta text-gray-700 dark:text-gray-300">{label}</span>
              <button
                type="button"
                onClick={() => {
                  const next = !form[key];
                  setForm((p) => ({ ...p, [key]: next }));
                  track('settings.alert_preference_changed', 'action', { channel, enabled: next });
                }}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  form[key] ? 'bg-primary' : 'bg-gray-200 dark:bg-white/10'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    form[key] ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </label>
          ))}
        </div>
      </div>

      {selectableRoles.length > 1 && (
        <div>
          <h4 className="text-title text-gray-900 dark:text-white mb-1">
            Primary Role
          </h4>
          <p className="text-meta text-gray-500 dark:text-gray-400 mb-3">
            Sets the role used to personalise your dashboards and project overview.
          </p>
          <div className="flex flex-wrap gap-2">
            {selectableRoles.map((role) => {
              const isSelected = (effectiveActiveRole ?? selectableRoles[0]) === role;
              return (
                <button
                  key={role}
                  type="button"
                  onClick={() => handleRoleSelect(role)}
                  className={`px-3 py-1.5 rounded-xl text-meta font-medium border transition-colors capitalize ${
                    isSelected
                      ? 'bg-primary text-white border-primary'
                      : 'bg-gray-50 dark:bg-white/5 border-gray-200 dark:border-white/10 text-gray-600 dark:text-gray-400 hover:border-primary/40'
                  }`}
                >
                  {ROLE_LABELS[role] ?? role.replace(/_/g, ' ')}
                </button>
              );
            })}
          </div>
          <p className="text-meta text-gray-400 dark:text-gray-500 mt-2">
            Sets your primary role across all dashboards and the project overview.
          </p>
        </div>
      )}
      </div>
      </div>

      {(avatarError || mutation.isError) && (
        <p className="text-meta text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 rounded-lg p-3">
          {avatarError ?? (mutation.error as Error).message}
        </p>
      )}

      {mutation.isSuccess && (
        <p className="text-meta text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-900/30 rounded-lg p-3">
          Settings saved successfully.
        </p>
      )}

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-meta font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
      >
        <Save className="h-4 w-4" />
        {mutation.isPending ? 'Saving...' : 'Save Changes'}
      </motion.button>
    </div>
  );
}
